import { Overlay } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    Component,
    Signal,
    WritableSignal,
    computed,
    effect,
    signal,
    untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_SELECT_SCROLL_STRATEGY } from '@angular/material/select';
import { TRANSLOCO_SCOPE, Translation, TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { Area } from 'app/api';
import { DialogBuilderComponent } from 'app/core/dialogBuilder/dialog-builder.component';
import { NewMDOService } from 'app/services/new_mdo.service';
import { cloneDeep } from 'lodash';
import { ProcessFilterService } from '../process-filter.service';
import { ProcessFilter } from '../process-filter.types';

@Component({
    selector: 'processes-filter-dialog',
    imports: [TranslocoModule, ReactiveFormsModule, FormlyModule],
    templateUrl: './processes-filter-dialog.component.html',
    styleUrl: './processes-filter-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: MAT_SELECT_SCROLL_STRATEGY,
            useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.reposition(),
            deps: [Overlay],
        },
        { provide: TRANSLOCO_SCOPE, useValue: 'bp' },
    ],
})
export class ProcessesFilterDialogComponent extends DialogBuilderComponent<ProcessFilter> {
    private _translocoContent: Signal<Translation> = toSignal(
        this._translocoService.selectTranslation('bp')
    );

    form = new FormGroup({});
    model?: any;
    fields: WritableSignal<FormlyFieldConfig[]> = signal([]);

    activeFilters: Array<any>;

    areas: Signal<Area[]> = computed(() => {
        let areas = [];
        if (this._newMdoService.areas()) {
            areas = cloneDeep(this._newMdoService.areas())
                .filter(area => area.status === 'ACTIVE')
                .map(area => {
                    return {
                        id: area.id,
                        name: area.title,
                    };
                });
        }
        return areas;
    });

    constructor(
        private _translocoService: TranslocoService,
        private _processFilterService: ProcessFilterService,
        private _newMdoService: NewMDOService
    ) {
        super();
        this.initializeForm();

        effect(() => {
            const filterValues = this._processFilterService.filterValues();
            if (filterValues) {
                this.form.patchValue(filterValues);
            }
        });

        effect(() => {
            const code = this.executeActionSignal();
            if (code && code === 'filter') {
                this._processFilterService.updateFilterValues(this.form.value);
                this.closeDialog();
            } else if (code && code === 'reset') {
                this._processFilterService.updateFilterValues({
                    status: [],
                    owner: [],
                    authGroup: [],
                    areas: [],
                    filterToActive: undefined,
                });
                this.closeDialog();
            }
        });
    }

    initializeForm() {
        this.fields.set([
            {
                key: 'status',
                type: 'select',
                defaultValue: untracked(this._processFilterService.filterValues).status,
                props: {
                    label: this._translocoContent()['status'],
                    multiple: true,
                    selectAllOption: this._translocoContent()['selectAll'],
                    options: this._newMdoService.statuses,
                },
            },
            {
                type: 'select',
                key: 'owner',
                defaultValue: untracked(this._processFilterService.filterValues).owner,
                props: {
                    label: this._translocoContent()['owner'],
                    options: this._newMdoService.usersForSelect,
                    selectAllOption: this._translocoContent()['selectAll'],
                    multiple: true,
                },
            },
            {
                type: 'select',
                key: 'authGroup',
                defaultValue: untracked(this._processFilterService.filterValues).authGroup,
                props: {
                    label: this._translocoContent()['authGroup'],
                    options: this._newMdoService.departments,
                    selectAllOption: this._translocoContent()['selectAll'],
                    multiple: true,
                    valueProp: 'id',
                    labelProp: 'text',
                },
            },
            {
                type: 'select',
                key: 'areas',
                defaultValue: untracked(this._processFilterService.filterValues).authGroup,
                props: {
                    label: this._translocoContent()['area'],
                    options: this.areas(),
                    selectAllOption: this._translocoContent()['selectAll'],
                    multiple: true,
                    valueProp: 'id',
                    labelProp: 'name',
                },
            },
            {
                type: 'checkbox',
                key: 'filterToActive',
                props: {
                    label: this._translocoContent()['filterToActive'],
                },
            },
        ]);
    }
}
